import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Eye, MessageSquare, Phone, Heart, Calendar, TrendingUp } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';

interface PerformanceData {
  date: string;
  views_count: number;
  leads_count: number;
  contacts_count: number;
  favorites_count: number;
}

interface PropertyPerformanceDashboardProps {
  propertyId: string;
  days?: number;
}

export const PropertyPerformanceDashboard: React.FC<PropertyPerformanceDashboardProps> = ({ 
  propertyId, 
  days = 30 
}) => {
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totals, setTotals] = useState({
    views: 0,
    leads: 0,
    contacts: 0,
    favorites: 0
  });

  const fetchPerformanceData = async () => {
    try {
      setIsLoading(true);
      
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data, error } = await supabase
        .from('property_performance_tracking')
        .select('*')
        .eq('property_id', propertyId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) {
        console.error('Error fetching performance data:', error);
        return;
      }

      if (data) {
        setPerformanceData(data);
        
        const totalViews = data.reduce((sum, item) => sum + (item.views_count || 0), 0);
        const totalLeads = data.reduce((sum, item) => sum + (item.leads_count || 0), 0);
        const totalContacts = data.reduce((sum, item) => sum + (item.contacts_count || 0), 0);
        const totalFavorites = data.reduce((sum, item) => sum + (item.favorites_count || 0), 0);
        
        setTotals({
          views: totalViews,
          leads: totalLeads,
          contacts: totalContacts,
          favorites: totalFavorites
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConversionRate = () => {
    if (totals.views === 0) return '0%';
    return ((totals.leads / totals.views) * 100).toFixed(1) + '%';
  };

  const getPerformanceLevel = () => {
    const conversionRate = totals.views > 0 ? (totals.leads / totals.views) * 100 : 0;
    
    if (conversionRate >= 5) return { level: 'Excelente', color: 'bg-green-100 text-green-800' };
    if (conversionRate >= 2) return { level: 'Bom', color: 'bg-yellow-100 text-yellow-800' };
    return { level: 'Pode melhorar', color: 'bg-red-100 text-red-800' };
  };

  useEffect(() => {
    fetchPerformanceData();
  }, [propertyId, days]);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="p-4 bg-muted rounded-lg">
                <div className="h-4 bg-muted-foreground/20 rounded mb-2"></div>
                <div className="h-6 bg-muted-foreground/20 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  const performance = getPerformanceLevel();

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Performance ({days} dias)
        </h3>
        <Badge className={performance.color}>
          {performance.level}
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Visualizações</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{totals.views.toLocaleString()}</div>
        </div>

        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-900">Leads</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{totals.leads.toLocaleString()}</div>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">Contatos</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">{totals.contacts.toLocaleString()}</div>
        </div>

        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-900">Favoritos</span>
          </div>
          <div className="text-2xl font-bold text-red-900">{totals.favorites.toLocaleString()}</div>
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">Taxa de Conversão</span>
        </div>
        <div className="text-lg font-bold text-gray-900">{getConversionRate()}</div>
      </div>

      {performanceData.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Últimos {Math.min(7, performanceData.length)} dias
          </h4>
          <div className="space-y-2">
            {performanceData.slice(0, 7).map((day) => (
              <div key={day.date} className="flex items-center justify-between text-sm py-2 border-b border-gray-200 last:border-0">
                <span className="text-gray-600">
                  {new Date(day.date).toLocaleDateString('pt-BR', { 
                    day: '2-digit', 
                    month: 'short' 
                  })}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-blue-600">{day.views_count || 0} views</span>
                  <span className="text-green-600">{day.leads_count || 0} leads</span>
                  {day.contacts_count > 0 && (
                    <span className="text-purple-600">{day.contacts_count} contatos</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {performanceData.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Nenhum dado de performance disponível ainda.</p>
          <p className="text-sm">Os dados aparecerão conforme houver interações com o imóvel.</p>
        </div>
      )}
    </Card>
  );
};